import { useEffect, useRef } from 'react';

export const useMentionSearch = (text: string, searchFunction: (query: string) => void, delay = 300) => {
  const timeoutRef = useRef(null);
  const lastSearchedRef = useRef<string>(null); // Tracks the last term searched
  const lastInsertedRef = useRef<string>(null); // Tracks mentions that were already resolved

  useEffect(() => {
    if (!text) return;

    const regex = /(?:^|\s)@([a-zA-Z0-9_]{3,})$/;
    const match = text.match(regex);

    if (!match) {
      clearTimeout(timeoutRef.current);
      return;
    }

    const term = match[1];

    // Avoid re-searching if it's the same term or was already resolved
    if (term === lastSearchedRef.current || term === lastInsertedRef.current) return;

    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      searchFunction(term);
      lastSearchedRef.current = term;
    }, delay);

    return () => clearTimeout(timeoutRef.current);
  }, [text, searchFunction, delay]);

  // Call this when a mention is selected, so it doesn't re-search
  const markMentionInserted = (username: string) => {
    lastInsertedRef.current = username;
    lastSearchedRef.current = null; // allow new searches later
  };

  return { markMentionInserted };
};
